import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { format } from "date-fns";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "@/components/ui/calendar.js";
import MapAutoComplete from "@/components/MapAutoComplete";
import {
  FiMapPin, FiCalendar, FiUserCheck, FiUser,
  FiSearch, FiMessageCircle, FiSmile, FiChevronDown, FiShield,
} from "react-icons/fi";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import QuickHubs from "@/components/QuickHubs";
import toast from "react-hot-toast";

export const SearchRide = () => {
  const navigator = useNavigate();
  const [date, setDate] = useState();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [femaleOnly, setFemaleOnly] = useState(false);
  const [errors, setErrors] = useState({});

  const isValidLocation = (value) => {
    if (!value || value.trim().length === 0) return true;
    return /[a-zA-Z0-9]{2,}/.test(value);
  };

  const isSameLocation = (locA, locB) => {
    if (!locA || !locB) return false;
    const a = locA.trim().toLowerCase();
    const b = locB.trim().toLowerCase();
    return a !== "" && b !== "" && a === b;
  };

  const handleFromChange = (newFrom) => {
    setFrom(newFrom);
    if (isSameLocation(newFrom, to)) {
      setErrors((prev) => ({
        ...prev,
        from: undefined,
        to: "Pickup and drop-off locations cannot be the same",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        from: undefined,
        ...(prev.to === "Pickup and drop-off locations cannot be the same" ? { to: undefined } : {}),
      }));
    }
  };

  const handleToChange = (newTo) => {
    setTo(newTo);
    if (isSameLocation(from, newTo)) {
      setErrors((prev) => ({
        ...prev,
        to: "Pickup and drop-off locations cannot be the same",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        to: undefined,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (from && !isValidLocation(from)) {
      newErrors.from = "Please enter a valid location (not just special characters or emojis)";
    }
    if (to && !isValidLocation(to)) {
      newErrors.to = "Please enter a valid location (not just special characters or emojis)";
    }
    if (from && to && isSameLocation(from, to)) {
      newErrors.to = "Pickup and drop-off locations cannot be the same";
      toast.error("Pickup and drop-off locations cannot be the same");
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const dateStr = date ? format(date, "yyyy-MM-dd") : "";
    navigator(`/ride-details/search?from=${from}&to=${to}&date=${dateStr}&femaleOnly=${femaleOnly}`);
  };

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex flex-col items-center min-h-screen w-full z-10">

      {/* ── Hero: vertically centered, no scroll ── */}
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] w-full px-4 py-6">

        {/* Page title — above the card, not inside it */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white tracking-tight drop-shadow">
            Search for a ride
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Leave fields blank to view all available rides
          </p>
        </div>

        {/* Search card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl border border-zinc-100 w-full max-w-lg overflow-hidden"
        >
          {/* From / To — side by side */}
          <div className="grid grid-cols-2 divide-x divide-zinc-100 border-b border-zinc-100">
            {/* From */}
            <div className="p-4 space-y-1.5">
              <Label htmlFor="from" className="flex items-center gap-1.5 text-zinc-500 text-xs font-semibold uppercase tracking-widest">
                <FiMapPin className="w-3 h-3 text-zinc-400" />
                From
              </Label>
              <MapAutoComplete value={from} changeHandler={handleFromChange} placeholder="Pickup location" />
              {errors.from && <p className="text-red-500 text-[10px] font-medium">{errors.from}</p>}
            </div>

            {/* To */}
            <div className="p-4 space-y-1.5">
              <Label htmlFor="to" className="flex items-center gap-1.5 text-zinc-500 text-xs font-semibold uppercase tracking-widest">
                <FiMapPin className="w-3 h-3 text-amber-500" />
                To
              </Label>
              <MapAutoComplete value={to} changeHandler={handleToChange} placeholder="Destination" />
              {errors.to && <p className="text-red-500 text-[10px] font-medium">{errors.to}</p>}
            </div>
          </div>

          {/* Quick Hubs — 2-column, compact, no redundant box */}
          <div className="grid grid-cols-2 divide-x divide-zinc-100 border-b border-zinc-100 bg-zinc-50/60">
            <div className="px-4 py-3">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Pickup hubs</p>
              <QuickHubs compact currentValue={from} disabledValue={to} onSelect={(hub) => handleFromChange(hub.address)} />
            </div>
            <div className="px-4 py-3">
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Drop-off hubs</p>
              <QuickHubs compact currentValue={to} disabledValue={from} onSelect={(hub) => handleToChange(hub.address)} />
            </div>
          </div>

          {/* Date + Female Only + Search button */}
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Date */}
              <div className="space-y-1.5">
                <Label htmlFor="date" className="flex items-center gap-1.5 text-zinc-500 text-xs font-semibold uppercase tracking-widest">
                  <FiCalendar className="w-3 h-3 text-zinc-400" />
                  Travel Date
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={
                        "w-full justify-start text-left h-9 rounded-lg border-zinc-200 text-xs font-medium" +
                        (!date ? " text-zinc-400" : " text-zinc-800")
                      }
                      type="button"
                    >
                      {date ? format(date, "MMM d, yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(day) => day < new Date(new Date().setHours(0, 0, 0, 0))}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Female Only */}
              <div className="space-y-1.5">
                <Label className="text-zinc-500 text-xs font-semibold uppercase tracking-widest block">
                  Filter
                </Label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={femaleOnly}
                  onClick={() => setFemaleOnly(!femaleOnly)}
                  className={`w-full h-9 rounded-lg border text-xs font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer ${
                    femaleOnly
                      ? "bg-pink-50 border-pink-200 text-pink-700"
                      : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300"
                  }`}
                >
                  <FiShield className={`w-3.5 h-3.5 ${femaleOnly ? "text-pink-500" : "text-zinc-400"}`} />
                  Female Only {femaleOnly && "✓"}
                </button>
              </div>
            </div>

            {/* Search */}
            <Button
              type="submit"
              className="w-full h-11 bg-zinc-900 hover:bg-amber-500 hover:text-zinc-900 text-white font-semibold text-sm rounded-lg transition-colors cursor-pointer active:scale-[0.99] flex items-center justify-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Search Rides
            </Button>
          </div>
        </form>

        {/* Scroll hint */}
        <button
          type="button"
          onClick={scrollToHowItWorks}
          className="mt-8 flex flex-col items-center gap-1 text-white/50 hover:text-white/80 transition-colors text-xs cursor-pointer group"
        >
          <span>How it works</span>
          <FiChevronDown className="w-4 h-4 animate-bounce" />
        </button>
      </div>

      {/* ── How It Works ── */}
      <div id="how-it-works" className="w-full bg-white py-20 px-4 md:px-8 border-t border-zinc-100">
        <div className="max-w-5xl mx-auto space-y-16">

          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-4xl font-bold text-zinc-900 tracking-tight">
              How to use <span className="text-amber-500">Travel Buddy</span>
            </h2>
            <p className="text-zinc-500 max-w-xl mx-auto text-base leading-relaxed">
              A community ride-matching platform exclusively for MUJ students and faculty. No vehicle required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { icon: <FiUserCheck className="w-6 h-6" />, title: "Sign Up", body: "Register using your MUJ email ID and get verified automatically." },
              { icon: <FiUser className="w-6 h-6" />, title: "Set Profile", body: "Add your name and usual commute area." },
              { icon: <FiSearch className="w-6 h-6" />, title: "Host or Join", body: "Post your route or browse rides that match your timing." },
              { icon: <FiMessageCircle className="w-6 h-6" />, title: "Connect", body: "Both parties receive each other's WhatsApp once accepted." },
              { icon: <FiSmile className="w-6 h-6" />, title: "Travel", body: "Split the fare and enjoy a stress-free commute." },
            ].map((step, i) => (
              <div key={step.title} className="flex flex-col items-center text-center gap-3 p-5 rounded-xl bg-zinc-50 border border-zinc-100 hover:border-zinc-200 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-white border border-zinc-200 flex items-center justify-center text-amber-500 shadow-sm">
                  {step.icon}
                </div>
                <div>
                  <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Step {i + 1}</p>
                  <h3 className="text-sm font-bold text-zinc-900">{step.title}</h3>
                  <p className="text-zinc-500 text-xs leading-relaxed mt-1">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-zinc-900 mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full divide-y divide-zinc-100">
              {[
                {
                  value: "item-1",
                  q: "Do I need to own a vehicle?",
                  a: <><em>No vehicle? No problem.</em> Whether you book a cab, an auto, or any other mode of transport, you can still host or join a ride. If you own a vehicle, you can host rides with it too.</>,
                },
                {
                  value: "item-2",
                  q: "Who can sign up?",
                  a: "Any student or faculty member with a valid MUJ email ID. The platform is exclusive to the MUJ community.",
                },
                {
                  value: "item-3",
                  q: "How is the cost determined?",
                  a: "The host sets the per-head cost upfront. You see the exact amount before sending a join request — no surprises.",
                },
                {
                  value: "item-4",
                  q: "Is it safe to ride with strangers?",
                  a: "Every user is verified through their MUJ credentials, so you're always riding within a trusted university network.",
                },
                {
                  value: "item-5",
                  q: "What if the host cancels the ride?",
                  a: "If a ride is cancelled, you'll be notified and can browse other available requests heading your way.",
                },
              ].map((faq) => (
                <AccordionItem key={faq.value} value={faq.value} className="py-1 border-none">
                  <AccordionTrigger className="text-left font-semibold text-sm text-zinc-800 hover:text-amber-500 transition-colors hover:no-underline py-3">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-500 text-sm leading-relaxed pb-3">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SearchRide;
